import type { FrameTime, PropertyDescriptor, SourceContext, SourceTypeDefinition } from '../core/definitions';
import { BaseSourceRuntime, str } from './base';
import { hasRef, loadBlob, validateMediaUrl, type MediaRef } from './assetLoad';
import { SourceLoadError, toMapped } from './errors';

export type ImageSettings = { assetId: string; url: string };

/** Leading bytes read for sniffing; SVG may open with an XML prolog and comments. */
export const SNIFF_BYTES = 512;

/**
 * Image format from magic bytes (blob.type is often empty for fetched files and
 * for assets stored before type sniffing), or null when it is not an image.
 */
export function sniffImageType(bytes: Uint8Array): string | null {
  const ascii = (from: number, to: number) => String.fromCharCode(...bytes.subarray(from, to));
  if (bytes.length >= 6 && ascii(0, 4) === 'GIF8') return 'image/gif';
  if (bytes.length >= 8 && bytes[0] === 0x89 && ascii(1, 4) === 'PNG') return 'image/png';
  if (bytes.length >= 12 && ascii(0, 4) === 'RIFF' && ascii(8, 12) === 'WEBP') return 'image/webp';
  if (bytes.length >= 12 && ascii(4, 8) === 'ftyp' && (ascii(8, 12) === 'avif' || ascii(8, 12) === 'avis')) return 'image/avif';
  if (bytes.length >= 3 && bytes[0] === 0xff && bytes[1] === 0xd8) return 'image/jpeg';
  if (bytes.length >= 14 && ascii(0, 2) === 'BM') return 'image/bmp';
  const text = new TextDecoder().decode(bytes.subarray(0, SNIFF_BYTES)).replace(/^\uFEFF/, '').trimStart();
  if (/^(<\?xml[^>]*>\s*)?(<!--[\s\S]*?-->\s*)*(<!DOCTYPE svg[^>]*>\s*)?<svg[\s>]/i.test(text)) return 'image/svg+xml';
  return null;
}

/**
 * Longest texture side we upload. WebGL2 guarantees far less, but every GPU
 * the studio targets offers at least this; larger images are downscaled once
 * at decode and laid out at their natural size.
 */
export const MAX_TEXTURE_SIDE = 8192;
/** SVG raster scale ceiling: beyond this the canvas costs more than the sharpness is worth. */
const MAX_SVG_RASTER_SCALE = 8;

const ANIMATION_CAPABLE = new Set(['image/gif', 'image/png', 'image/webp', 'image/avif']);
const DEFAULT_FRAME_MS = 100;

interface Animation {
  decoder: ImageDecoder;
  canvas: HTMLCanvasElement;
  frameCount: number;
  durations: number[];
  index: number;
  elapsed: number;
  pending: boolean;
}

const FALLBACK_SVG_SIZE = { width: 300, height: 150 };

/**
 * Layout size of an SVG whose root lacks width or height: its viewBox, else the
 * browser default (300x150). null when both are declared, so the <img> size
 * stands. Browsers otherwise size such a file as 300 wide by the viewBox aspect.
 */
export function svgFallbackSize(svgText: string): { width: number; height: number } | null {
  const root = /<svg\b[^>]*>/i.exec(svgText)?.[0] ?? '';
  if (/\swidth\s*=/i.test(root) && /\sheight\s*=/i.test(root)) return null;
  const viewBox = /\bviewBox\s*=\s*["']([^"']+)["']/i.exec(root)?.[1];
  const parts = viewBox?.trim().split(/[\s,]+/).map(Number);
  if (parts && parts.length === 4 && parts[2] > 0 && parts[3] > 0 && parts.every(Number.isFinite)) {
    return { width: Math.round(parts[2]), height: Math.round(parts[3]) };
  }
  return FALLBACK_SVG_SIZE;
}

const XLINK = 'http://www.w3.org/1999/xlink';

/**
 * Removes what an SVG <img> refuses to draw or would show as a placeholder:
 * <foreignObject> (it taints the canvas, so the GPU upload fails and nothing
 * shows), scripts, event handlers, external references and CSS imports. Safety
 * does not depend on this: the <img> context never runs scripts or fetches.
 * Returns the input unchanged when it does not parse as SVG.
 */
export function drawableSvg(text: string): string {
  const doc = new DOMParser().parseFromString(text, 'image/svg+xml');
  const root = doc.documentElement;
  if (root.nodeName.toLowerCase() !== 'svg' || doc.getElementsByTagName('parsererror').length > 0) return text;
  for (const el of [...root.querySelectorAll('script, foreignObject')]) el.remove();
  for (const el of [root, ...root.querySelectorAll('*')]) {
    for (const attr of [...el.attributes]) if (attr.name.toLowerCase().startsWith('on')) el.removeAttribute(attr.name);
    const href = el.getAttribute('href') ?? el.getAttributeNS(XLINK, 'href');
    if (href !== null && !href.startsWith('#') && !href.startsWith('data:')) el.remove();
  }
  for (const style of root.querySelectorAll('style')) {
    style.textContent = (style.textContent ?? '').replace(/@import[^;]*;?/gi, '').replace(/url\(\s*['"]?(?!#|data:)[^)]*\)/gi, 'none');
  }
  return new XMLSerializer().serializeToString(doc);
}

interface Vector {
  /** False for raster formats only <img> could decode: those are drawn once at natural size. */
  svg: boolean;
  img: HTMLImageElement;
  url: string;
  width: number;
  height: number;
  canvas: HTMLCanvasElement;
  scale: number;
}

/**
 * Decodes an SVG through an <img> from a blob URL. That context never runs the
 * document's scripts and never fetches external resources, which is what keeps
 * untrusted SVG safe; it must not move to <object>, <iframe> or inline DOM.
 */
async function openVector(blob: Blob, svg: boolean): Promise<Vector> {
  // The type is set explicitly: an untyped blob URL is not sniffed as SVG.
  const url = URL.createObjectURL(svg ? new Blob([drawableSvg(await blob.text())], { type: 'image/svg+xml' }) : blob);
  const img = new Image();
  img.decoding = 'async';
  img.src = url;
  try {
    await new Promise<void>((resolve, reject) => {
      img.onload = () => resolve();
      img.onerror = () => reject(new SourceLoadError('load-failed', 'The image could not be decoded'));
    });
  } catch (err) {
    URL.revokeObjectURL(url);
    throw err;
  }
  let { naturalWidth: width, naturalHeight: height } = img;
  if (svg) {
    const declared = svgFallbackSize(await blob.slice(0, 64 * 1024).text());
    if (declared) ({ width, height } = declared);
  }
  if (!width || !height) {
    URL.revokeObjectURL(url);
    throw new SourceLoadError('load-failed', 'The image has no size');
  }
  return { svg, img, url, width, height, canvas: document.createElement('canvas'), scale: 0 };
}

/** Rasterises the vector at `scale` × its layout size, capped by the texture limit. Returns the ratio used. */
function drawVector(v: Vector, scale: number): number {
  const ratio = Math.min(scale, MAX_TEXTURE_SIDE / v.width, MAX_TEXTURE_SIDE / v.height);
  v.canvas.width = Math.max(1, Math.round(v.width * ratio));
  v.canvas.height = Math.max(1, Math.round(v.height * ratio));
  const g = v.canvas.getContext('2d');
  g?.clearRect(0, 0, v.canvas.width, v.canvas.height);
  g?.drawImage(v.img, 0, 0, v.canvas.width, v.canvas.height);
  v.scale = scale;
  return v.canvas.width / v.width;
}

export class ImageRuntime extends BaseSourceRuntime<ImageSettings> {
  private ref: MediaRef;
  private bitmap: ImageBitmap | null = null;
  private anim: Animation | null = null;
  private vector: Vector | null = null;
  private rasterScale = 1;

  constructor(ctx: SourceContext, settings: Partial<ImageSettings>) {
    super(ctx);
    this.ref = { assetId: str(settings.assetId, ''), url: str(settings.url, '') };
    void this.load();
  }

  update(settings: ImageSettings): void {
    if (this.destroyed) return;
    const next = { assetId: str(settings.assetId, ''), url: str(settings.url, '') };
    if (next.assetId === this.ref.assetId && next.url === this.ref.url) return;
    this.ref = next;
    void this.load();
  }

  invoke(action: string): void {
    if (action === 'retry' && !this.destroyed) void this.load();
  }

  setRasterScale(scale: number): void {
    if (!Number.isFinite(scale) || scale <= 0) return;
    this.rasterScale = scale;
    const v = this.vector;
    if (!v?.svg || this.destroyed) return;
    const target = this.vectorScale();
    if (target !== v.scale) this.publishVector(v, target);
  }

  /** Never below 1 so a scaled-down logo keeps its natural-size raster, which the GPU minifies cleanly. */
  private vectorScale(): number {
    return Math.min(MAX_SVG_RASTER_SCALE, Math.max(1, this.rasterScale));
  }

  private publishVector(v: Vector, scale: number): void {
    const ratio = drawVector(v, scale);
    this.publishVideo(v.canvas, v.width, v.height, false, ratio);
  }

  private publishBitmap(bitmap: ImageBitmap, width: number, height: number): void {
    this.publishVideo(bitmap, width, height, false, bitmap.width / width);
  }

  tick(time: FrameTime): void {
    const a = this.anim;
    if (!a || a.pending || this.destroyed) return;
    a.elapsed += time.deltaMs;
    const current = a.durations[a.index] ?? DEFAULT_FRAME_MS;
    if (a.elapsed < current) return;
    // Keep the remainder so slow frames do not accumulate drift, but never fast-forward through several frames.
    a.elapsed = Math.min(a.elapsed - current, current);
    const next = (a.index + 1) % a.frameCount;
    a.pending = true;
    a.decoder
      .decode({ frameIndex: next })
      .then(({ image }) => {
        if (this.anim !== a) {
          image.close();
          return;
        }
        this.drawFrame(a, image, next);
        a.index = next;
        a.pending = false;
        this.touchVideo();
      })
      .catch((err) => {
        if (this.anim !== a) return;
        // Skip the bad frame instead of leaving `pending` set, which would freeze the animation for good.
        a.index = next;
        a.pending = false;
        this.log('warn', `animation frame ${next} could not be decoded; skipped`, err);
      });
  }

  private drawFrame(a: Animation, frame: VideoFrame, index: number): void {
    const ctx = a.canvas.getContext('2d');
    if (a.canvas.width !== frame.displayWidth || a.canvas.height !== frame.displayHeight) {
      a.canvas.width = frame.displayWidth;
      a.canvas.height = frame.displayHeight;
    }
    ctx?.clearRect(0, 0, a.canvas.width, a.canvas.height);
    ctx?.drawImage(frame, 0, 0);
    // VideoFrame.duration is in microseconds; browsers treat 0 as "unspecified".
    a.durations[index] = frame.duration ? frame.duration / 1000 : DEFAULT_FRAME_MS;
    frame.close();
  }

  private disposeContent(): void {
    this.bitmap?.close();
    this.bitmap = null;
    this.anim?.decoder.close();
    this.anim = null;
    if (this.vector) {
      URL.revokeObjectURL(this.vector.url);
      this.vector.img.removeAttribute('src');
      this.vector.canvas.width = 0;
      this.vector = null;
    }
    this.clearVideo();
  }

  private async load(): Promise<void> {
    const epoch = this.beginEpoch();
    this.disposeContent();
    if (!hasRef(this.ref)) {
      this.setStatus('idle');
      return;
    }
    this.setStatus('starting');
    try {
      const blob = await loadBlob(this.ctx, this.ref);
      if (!this.isCurrent(epoch)) return;
      const head = new Uint8Array(await blob.slice(0, SNIFF_BYTES).arrayBuffer());
      const type = sniffImageType(head) ?? blob.type;
      if (!this.isCurrent(epoch)) return;

      if (ANIMATION_CAPABLE.has(type) && typeof ImageDecoder !== 'undefined' && (await ImageDecoder.isTypeSupported(type))) {
        if (await this.tryAnimated(blob, type, epoch)) return;
        if (!this.isCurrent(epoch)) return;
      } else if (type === 'image/gif') {
        this.log('warn', 'ImageDecoder is unavailable in this browser: animated GIFs show their first frame only');
      }

      // createImageBitmap rejects SVG blobs; an <img> rasterises them.
      let bitmap = type === 'image/svg+xml' ? null : await createImageBitmap(blob).catch(() => null);
      if (!this.isCurrent(epoch)) {
        bitmap?.close();
        return;
      }
      if (bitmap) {
        const { width, height } = bitmap;
        const fit = Math.min(1, MAX_TEXTURE_SIDE / width, MAX_TEXTURE_SIDE / height);
        if (fit < 1) {
          const full = bitmap;
          bitmap = await createImageBitmap(full, {
            resizeWidth: Math.max(1, Math.round(width * fit)),
            resizeHeight: Math.max(1, Math.round(height * fit)),
            resizeQuality: 'high',
          }).finally(() => full.close());
          this.log('info', `image is ${width}×${height}; uploaded at ${bitmap.width}×${bitmap.height} (texture limit ${MAX_TEXTURE_SIDE})`);
          if (!this.isCurrent(epoch)) {
            bitmap.close();
            return;
          }
        }
        this.bitmap = bitmap;
        this.publishBitmap(bitmap, width, height);
      } else {
        const svg = type === 'image/svg+xml' || blob.type === 'image/svg+xml';
        const vector = await openVector(blob, svg);
        if (!this.isCurrent(epoch)) {
          URL.revokeObjectURL(vector.url);
          return;
        }
        this.vector = vector;
        this.publishVector(vector, svg ? this.vectorScale() : 1);
      }
      this.setStatus('live');
    } catch (err) {
      if (!this.isCurrent(epoch)) return;
      const mapped = toMapped(err);
      this.fail(mapped.code === 'unknown' ? 'load-failed' : mapped.code, mapped.message);
    }
  }

  /** Returns true when the image has several frames and animation is now running. */
  private async tryAnimated(blob: Blob, type: string, epoch: number): Promise<boolean> {
    const decoder = new ImageDecoder({ data: blob.stream(), type });
    try {
      await decoder.tracks.ready;
      await decoder.completed;
      const frameCount = decoder.tracks.selectedTrack?.frameCount ?? 1;
      if (frameCount <= 1) {
        decoder.close();
        return false;
      }
      const { image } = await decoder.decode({ frameIndex: 0 });
      if (!this.isCurrent(epoch)) {
        image.close();
        decoder.close();
        return true;
      }
      const anim: Animation = { decoder, canvas: document.createElement('canvas'), frameCount, durations: [], index: 0, elapsed: 0, pending: false };
      this.drawFrame(anim, image, 0);
      this.anim = anim;
      this.publishCanvas(anim.canvas);
      this.setStatus('live');
      return true;
    } catch (err) {
      decoder.close();
      this.log('warn', 'ImageDecoder failed, falling back to the first frame', err);
      return false;
    }
  }

  protected release(): void {
    this.disposeContent();
  }
}

export const imageDefinition: SourceTypeDefinition<ImageSettings> = {
  kind: 'image',
  displayName: 'Image',
  caps: { video: true, audio: false, retry: true },
  category: 'media',
  defaults: () => ({ assetId: '', url: '' }),
  properties: (): PropertyDescriptor[] => [
    { key: 'assetId', label: 'Image file', type: 'asset', accept: 'image/*' },
    {
      key: 'url',
      label: 'or URL',
      type: 'text',
      inputType: 'url',
      applyOn: 'commit',
      validate: validateMediaUrl,
      placeholder: 'https://… (must allow cross-origin use)',
      visibleWhen: (s) => !s.assetId,
    },
  ],
  create: (ctx, settings) => new ImageRuntime(ctx, settings),
};
